/**
 * useRaceHost Hook
 *
 * Custom hook for managing race host state and interactions.
 * Handles the full race lifecycle: configure -> waiting -> countdown -> racing -> results
 *
 * Refactored to:
 * - Use useRaceStateMachine for predictable phase transitions
 * - Use usePolling for fallback data fetching
 * - Consolidate duplicate callback initialization
 * - Reduce ref usage through stable callbacks
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { User } from '@supabase/supabase-js';
import { RaceHostManager, RaceHostCallbacks } from '@/services/RaceHostManager';
import { useRaceStateMachine } from './useRaceStateMachine';
import { usePolling } from './useRealtimeWithFallback';
import type {
  RaceConfig,
  RaceParticipant,
  LeaderboardEntry,
} from '@/types/race';

export function useRaceHost(user: User | null) {
  // Use state machine for phase management
  const {
    phase,
    room,
    countdown,
    createRoom: smCreateRoom,
    resumeRoom: smResumeRoom,
    startCountdown,
    countdownTick,
    raceStarted,
    startRaceFailed,
    raceEnded,
    reset: smReset,
  } = useRaceStateMachine();

  // Data state
  const [participants, setParticipants] = useState<RaceParticipant[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  // Manager ref - stable across renders
  const managerRef = useRef<RaceHostManager | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * Factory function for creating manager callbacks.
   * Consolidated to avoid duplication between initial creation and newRace.
   */
  const createManagerCallbacks = useCallback((): RaceHostCallbacks => ({
    onParticipantsUpdate: (newParticipants) => {
      setParticipants(newParticipants);
    },
    onLiveStatsUpdate: async () => {
      // Update leaderboard when live stats change
      if (managerRef.current) {
        try {
          const newLeaderboard = await managerRef.current.getLeaderboard();
          setLeaderboard(newLeaderboard);
        } catch (err) {
          console.error('Error updating leaderboard on live stats:', err);
        }
      }
    },
    onParticipantFinish: async (participant) => {
      // Update participant in list
      setParticipants((prev) =>
        prev.map((p) => (p.id === participant.id ? participant : p))
      );
      // Update leaderboard
      if (managerRef.current) {
        try {
          const newLeaderboard = await managerRef.current.getLeaderboard();
          setLeaderboard(newLeaderboard);
        } catch (err) {
          console.error('Error updating leaderboard on participant finish:', err);
        }
      }
    },
    onError: (err) => setError(err),
    onConnectionChange: (connected) => setIsConnected(connected),
  }), []);

  // Initialize manager when user is available
  useEffect(() => {
    if (!user) return;

    // Skip recreation if manager already exists and has an active room
    if (managerRef.current && managerRef.current.getRoomId()) {
      console.log('⏭️ Skipping manager recreation - active room exists');
      return;
    }

    // Also skip if manager exists but no room yet (just reuse existing manager)
    if (managerRef.current) {
      console.log('⏭️ Reusing existing manager');
      return;
    }

    console.log('🆕 Creating new RaceHostManager');
    managerRef.current = new RaceHostManager(user.id, createManagerCallbacks());
  }, [user, createManagerCallbacks]);

  // Polling for participants during waiting phase (fallback for missed realtime events)
  const { stop: stopParticipantPolling, start: startParticipantPolling } = usePolling({
    fetchData: async () => {
      if (!managerRef.current) return [];
      return managerRef.current.getParticipants();
    },
    onData: setParticipants,
    interval: 3000,
    enabled: phase === 'waiting' && !isConnected,
  });

  // Polling for leaderboard during racing phase
  const { stop: stopLeaderboardPolling, start: startLeaderboardPolling } = usePolling({
    fetchData: async () => {
      if (!managerRef.current) return { leaderboard: [], participants: [] };
      const [newLeaderboard, newParticipants] = await Promise.all([
        managerRef.current.getLeaderboard(),
        managerRef.current.getParticipants(),
      ]);
      return { leaderboard: newLeaderboard, participants: newParticipants };
    },
    onData: ({ leaderboard: newLeaderboard, participants: newParticipants }) => {
      setLeaderboard(newLeaderboard);
      setParticipants(newParticipants);

      // Check if all finished
      const allFinished = newParticipants.length > 0 &&
        newParticipants.every((p) => p.status === 'finished');
      if (allFinished && managerRef.current) {
        // Auto-end race
        managerRef.current.endRace('all_finished').then(() => {
          stopLeaderboardPolling();
          raceEnded();
        });
      }
    },
    interval: 2000,
    enabled: phase === 'racing',
  });

  // Handle visibility change for reconnection
  useEffect(() => {
    const handleVisibilityChange = async () => {
      const roomId = managerRef.current?.getRoomId();
      if (document.visibilityState === 'visible' && roomId && managerRef.current) {
        console.log('🔄 Tab visible - reconnecting...', { roomId });
        setIsReconnecting(true);
        setError(null);

        // Fetch participants immediately
        try {
          const newParticipants = await managerRef.current.getParticipants();
          setParticipants(newParticipants);
          console.log('👥 Fetched participants on tab visible:', newParticipants.length);
        } catch (err) {
          console.error('Error fetching participants on visibility change:', err);
        }

        const result = await managerRef.current.reconnect();
        if (result.success) {
          console.log('✅ Reconnected successfully');
        } else {
          console.error('❌ Reconnection failed:', result.error);
        }

        setIsReconnecting(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Create room
  const createRoom = useCallback(async (config: RaceConfig) => {
    if (!managerRef.current) return;

    setError(null);
    setIsLoading(true);

    const result = await managerRef.current.createRoom(config);

    setIsLoading(false);

    if (result.success && result.room) {
      smCreateRoom(result.room);
      startParticipantPolling();
    } else {
      setError(result.error || 'Failed to create room');
    }
  }, [smCreateRoom, startParticipantPolling]);

  // Resume an existing room by ID
  const resumeRoom = useCallback(async (roomId: string) => {
    if (!managerRef.current) return;

    setError(null);
    setIsLoading(true);

    const result = await managerRef.current.resumeRoom(roomId);

    if (result.success && result.room) {
      const resumePhase = result.room.state === 'racing' ? 'racing' : 'waiting';
      smResumeRoom(result.room, resumePhase);

      if (resumePhase === 'racing') {
        const initialLeaderboard = await managerRef.current.getLeaderboard();
        setLeaderboard(initialLeaderboard);
        startLeaderboardPolling();
      } else {
        startParticipantPolling();
      }

      const initialParticipants = await managerRef.current.getParticipants();
      setParticipants(initialParticipants);
    } else {
      setError(result.error || 'Failed to resume room');
    }

    setIsLoading(false);
  }, [smResumeRoom, startLeaderboardPolling, startParticipantPolling]);

  // Start race with countdown
  const startRace = useCallback(async () => {
    if (!managerRef.current || participants.length === 0) {
      setError('Need at least one participant to start');
      return;
    }

    stopParticipantPolling();
    startCountdown();

    countdownIntervalRef.current = setInterval(() => {
      countdownTick();
    }, 1000);

    // Wait for countdown to complete (3 seconds)
    setTimeout(async () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }

      if (!managerRef.current) return;

      const result = await managerRef.current.startRace();
      if (result.success && result.room) {
        raceStarted(result.room);
        startLeaderboardPolling();
      } else {
        setError(result.error || 'Failed to start race');
        startRaceFailed();
        startParticipantPolling();
      }
    }, 3000);
  }, [
    participants.length,
    stopParticipantPolling,
    startCountdown,
    countdownTick,
    raceStarted,
    startRaceFailed,
    startLeaderboardPolling,
    startParticipantPolling,
  ]);

  // End race manually
  const endRace = useCallback(async () => {
    if (!managerRef.current) return;

    stopLeaderboardPolling();

    const result = await managerRef.current.endRace('host_ended');

    if (result.success) {
      const finalLeaderboard = await managerRef.current.getLeaderboard();
      setLeaderboard(finalLeaderboard);
      raceEnded();
    } else {
      setError(result.error || 'Failed to end race');
    }
  }, [stopLeaderboardPolling, raceEnded]);

  // Cancel race (from waiting room)
  const cancelRace = useCallback(async () => {
    stopLeaderboardPolling();
    stopParticipantPolling();

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    if (managerRef.current) {
      const result = await managerRef.current.cancelRace();
      if (!result.success) {
        console.error('Failed to cancel race:', result.error);
      }
      await managerRef.current.cleanup();
    }

    smReset();
    setParticipants([]);
    setLeaderboard([]);
    setError(null);
    setIsConnected(false);
  }, [stopLeaderboardPolling, stopParticipantPolling, smReset]);

  // Start new race (from results)
  const newRace = useCallback(async () => {
    stopLeaderboardPolling();
    stopParticipantPolling();

    if (managerRef.current) {
      await managerRef.current.cleanup();
    }

    // Reinitialize manager with consolidated callbacks
    if (user) {
      managerRef.current = new RaceHostManager(user.id, createManagerCallbacks());
    }

    smReset();
    setParticipants([]);
    setLeaderboard([]);
    setError(null);
    setIsReconnecting(false);
    setIsConnected(false);
  }, [user, stopLeaderboardPolling, stopParticipantPolling, smReset, createManagerCallbacks]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      stopLeaderboardPolling();
      stopParticipantPolling();
      managerRef.current?.cleanup();
    };
  }, [stopLeaderboardPolling, stopParticipantPolling]);

  return {
    phase,
    room,
    participants,
    leaderboard,
    countdown,
    error,
    isLoading,
    isReconnecting,
    isConnected,
    createRoom,
    resumeRoom,
    startRace,
    endRace,
    cancelRace,
    newRace,
  };
}
